import React, { useEffect, useState } from "react";
import { Modal, Button, Alert, Form } from "react-bootstrap";
import { Link } from "react-router-dom";

import { EntityRefBadge } from "../../components/EntityRef/EntityRefBadge";
import { JobRef } from "./JobRefBadge";
import modalStyles from "./modalLayout.module.scss";

const SAFE_MODAL_LAYOUT = {
  scrollable: true,
  dialogClassName: modalStyles.dialog,
  contentClassName: modalStyles.content,
} as const;

const MAX_REASON_LEN = 500;

export interface ReplaceSongAudioTarget {
  trackId: string;
  title: string;
  primaryArtistName?: string | null;
  primaryArtistId?: string | null;
  songType?: string | null;
  sourceProcessingJobId?: string | null;
  playbackVersionNumber?: number | null;
  approvedObjectKey?: string | null;
  checksumApproved?: string | null;
  file: File;
}

export function ReplaceSongAudioConfirmModal({
  show,
  target,
  busy,
  errorMessage,
  errorOpenPath,
  onCancel,
  onConfirm,
}: {
  show: boolean;
  target: ReplaceSongAudioTarget | null;
  busy: boolean;
  errorMessage: string;
  /** DRA-3 — when 409 replacement-already-in-progress, link to the open job. */
  errorOpenPath?: string | null;
  onCancel: () => void;
  onConfirm: (replaceReason: string) => void;
}): React.ReactElement {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (show) setReason("");
  }, [show, target?.trackId, target?.file?.name]);

  const trimmed = reason.trim();
  const reasonOk = trimmed.length > 0 && trimmed.length <= MAX_REASON_LEN;

  return (
    <Modal show={show} onHide={busy ? undefined : onCancel} size="lg" {...SAFE_MODAL_LAYOUT}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Replace Song Audio</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {target ? (
          <>
            <Alert variant="warning" className="mb-3">
              You are replacing the audio attached to this Song. The Song, Track
              ID, and all channel/programming relationships will remain unchanged.
              The current audio will continue playing until the replacement has
              completed processing and passed validation.
            </Alert>

            <dl className="row mb-3" style={{ fontSize: 13.5 }}>
              <dt className="col-sm-4">Existing Song title</dt>
              <dd className="col-sm-8">
                <strong>{target.title}</strong>
              </dd>

              <dt className="col-sm-4">Existing Primary Artist</dt>
              <dd className="col-sm-8">
                {target.primaryArtistName?.trim() ? (
                  target.primaryArtistName
                ) : target.primaryArtistId ? (
                  <EntityRefBadge kind="artist" id={target.primaryArtistId} />
                ) : (
                  "—"
                )}
              </dd>

              <dt className="col-sm-4">Track</dt>
              <dd className="col-sm-8">
                <EntityRefBadge kind="track" id={target.trackId} />
              </dd>

              <dt className="col-sm-4">Current Song Processing</dt>
              <dd className="col-sm-8">
                {target.sourceProcessingJobId ? (
                  <JobRef jobId={target.sourceProcessingJobId} />
                ) : (
                  <span className="text-muted">—</span>
                )}
              </dd>

              <dt className="col-sm-4">Current playback</dt>
              <dd className="col-sm-8">
                {target.playbackVersionNumber != null
                  ? `v${target.playbackVersionNumber} (remains live until cutover)`
                  : "Current Playback MP3 remains live until cutover"}
                {target.approvedObjectKey ? (
                  <div className="text-muted small" style={{ wordBreak: "break-all" }}>
                    {target.approvedObjectKey}
                  </div>
                ) : null}
                {target.checksumApproved ? (
                  <div className="text-muted small" style={{ wordBreak: "break-all" }}>
                    checksum {target.checksumApproved.slice(0, 12)}…
                  </div>
                ) : null}
              </dd>

              <dt className="col-sm-4">Replacement file</dt>
              <dd className="col-sm-8">
                {target.file.name}{" "}
                <span className="text-muted">
                  ({Math.max(1, Math.round(target.file.size / 1024))} KB)
                </span>
              </dd>
            </dl>

            <Alert variant="info" className="mb-3">
              <ul className="mb-0 ps-3">
                <li>
                  This uploads <strong>new source audio</strong> into a Track-linked
                  Song Processing job. It does <strong>not</strong> create a second
                  Song or Track.
                </li>
                <li>
                  Upload does <strong>not</strong> immediately overwrite the current
                  Playback MP3.
                </li>
                <li>
                  If processing fails before cutover, the current Playback MP3 and
                  Song identity stay unchanged.
                </li>
              </ul>
            </Alert>

            <Form.Group className="mb-2" controlId="replace-song-audio-reason">
              <Form.Label>
                Replacement reason <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={reason}
                disabled={busy}
                maxLength={MAX_REASON_LEN}
                placeholder="Why is this Song Audio being replaced?"
                onChange={(e) => setReason(e.target.value)}
                data-testid="replace-song-audio-reason"
              />
              <Form.Text muted>
                Required. {trimmed.length}/{MAX_REASON_LEN} characters.
              </Form.Text>
            </Form.Group>

            {errorMessage ? (
              <Alert variant="danger" className="mb-0" data-testid="replace-song-audio-error">
                <div>{errorMessage}</div>
                {errorOpenPath ? (
                  <div className="mt-2">
                    <Link to={errorOpenPath} data-testid="replace-song-audio-open-existing">
                      Open existing replacement attempt
                    </Link>
                  </div>
                ) : null}
              </Alert>
            ) : null}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={busy || !target || !reasonOk}
          onClick={() => onConfirm(trimmed)}
          data-testid="replace-song-audio-confirm"
        >
          {busy ? "Starting replacement…" : "Replace Song Audio"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
